import { useJuryReportHasUnsavedChanges } from "../designContest/jury";
import { relativeAdvantagesTooLong } from "../award/relativeAdvantages";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useMayCreateContract } from "../award/useMayCreateContract";
import { useAwardMayBeWithdrawn, useUnawardedMiniCompetitionRefused } from "../FrameworkNoLongerActive";
import { useCanManageSourcingEvent } from "../useCanManageSourcingEvent";
import {
  AwardLetterStatusDto,
  SeQuestionKind,
  SeQuestionStatus,
  SourcingEventStatus,
  useGetBuyerSeQuestionsQuery,
  useGetAwardLettersQuery,
  useGetEvaluationQuery,
  useListBidsForEventQuery,
  useListFrameworkAgreementsQuery,
  useListParticipationRequestsForBuyerQuery,
  type SourcingEventDto,
} from "../../../autogen/bff-api";
import {
  isSchemeProcedure,
  isTwoStageProcedure,
} from "../../sourcing-events/buyer/wizard/utils/procedure";
import { requireBooleanEnvVar } from "../../../config";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useCurrentMoment } from "../../sourcing-events/portal/utils/useCurrentMoment";
import { cannotBeAwardedUnderTheCeiling, useAwardSelection } from "../award/AwardSelectionContext";
import { useRoundSelection } from "../rounds/RoundSelectionContext";
import type { EventTab } from "../EventTabs";
import { isFrameworkAward, latestRoundOf, panelReadsBids, type NowPanelInput } from "./deriveNowPanel";

const EVALUATING_STATUSES: ReadonlySet<SourcingEventStatus> = new Set([
  SourcingEventStatus.Evaluation,
  SourcingEventStatus.Evaluated,
]);

const AWARDED_STATUSES: ReadonlySet<SourcingEventStatus> = new Set([
  SourcingEventStatus.AwardDecision,
  SourcingEventStatus.Awarded,
]);

export const useNowPanelData = (
  event: SourcingEventDto,
  activeTab: EventTab,
): NowPanelInput & { retryEvaluation: () => void } => {
  const eventId = event.id;
  const { rounds, roundsLoaded, roundsFailed, retryRounds } = useRoundSelection();
  const {
    winnerIds,
    standstillProblem,
    tooManyWinners,
    ceilingProblem,
    relativeAdvantages,
    endedContractWinners = [],
    unassessedWinners = [],
  } = useAwardSelection();
  const mayCreateContract = useMayCreateContract(event);
  const callOffRefusedByTermination = useAwardMayBeWithdrawn(event);
  const unawardedRefusedByFramework = useUnawardedMiniCompetitionRefused(event);
  const mayManage = useCanManageSourcingEvent(event);
  const { me } = useLoggedInWithOrgContextState();
  const now = useCurrentMoment().valueOf();
  const juryReportHasUnsavedChanges = useJuryReportHasUnsavedChanges(
    event.id,
    event.eventContext.type === "Public" ? event.eventContext.designContest : null,
  );

  const procedureType = event.eventContext.type === "Public" ? event.eventContext.procedureType : null;
  const collectsRequests = isTwoStageProcedure(procedureType) || isSchemeProcedure(procedureType);

  const { data: questionsData } = useGetBuyerSeQuestionsQuery(
    event.status === SourcingEventStatus.Published ? { eventId } : skipToken,
  );
  const { data: requestsData } = useListParticipationRequestsForBuyerQuery(
    collectsRequests ? { eventId } : skipToken,
  );
  const { data: bidsData, isSuccess: bidsLoaded } = useListBidsForEventQuery(
    panelReadsBids(event.status) ? { eventId } : skipToken,
  );
  // GetEvaluation without `round` evaluates the original competition's bids, not the latest round's.
  const readsEvaluation = EVALUATING_STATUSES.has(event.status);
  const {
    currentData: evaluation,
    isError: evaluationQueryFailed,
    refetch: refetchEvaluation,
  } = useGetEvaluationQuery(
    readsEvaluation && roundsLoaded ? { eventId, round: latestRoundOf(rounds)?.id } : skipToken,
  );
  const { data: awardLetters } = useGetAwardLettersQuery(
    AWARDED_STATUSES.has(event.status) ? { eventId } : skipToken,
  );

  const { data: frameworkAgreements, isSuccess: frameworkAgreementsLoaded } = useListFrameworkAgreementsQuery(
    AWARDED_STATUSES.has(event.status) && isFrameworkAward(event) ? {} : skipToken,
  );
  const frameworkAgreementLink = event.frameworkAgreementLink;
  const frameworkAgreementId = frameworkAgreementLink
    ? frameworkAgreementLink.mayOpen
      ? frameworkAgreementLink.frameworkAgreementId
      : undefined
    : frameworkAgreements?.frameworkAgreements?.find((framework) => framework.sourcingEventId === eventId)?.id;

  const unansweredQuestions = (questionsData?.questions ?? []).filter(
    (question) =>
      question.kind === SeQuestionKind.Question &&
      question.status === SeQuestionStatus.Unanswered,
  ).length;

  return {
    event,
    now,
    unansweredQuestions,
    participationRequests: requestsData ?? null,
    bids: bidsData?.bids ?? [],
    bidsLoaded,
    selectedWinnerCount: winnerIds.length,
    standstillChoiceIncomplete: standstillProblem != null,
    tooManyWinners,
    winnerContractEnded: endedContractWinners.length > 0,
    winnerAwaitsAssessment: unassessedWinners.length > 0,
    awardValueOverCeiling: ceilingProblem != null,
    tenderOverCeiling: ceilingProblem != null && cannotBeAwardedUnderTheCeiling(ceilingProblem),
    relativeAdvantagesTooLong: relativeAdvantagesTooLong(relativeAdvantages),
    evaluation: evaluation ?? null,
    evaluationLoaded: !readsEvaluation || evaluation != null,
    evaluationFailed: readsEvaluation && (roundsFailed || evaluationQueryFailed),
    retryEvaluation: () => {
      if (roundsFailed) retryRounds();
      if (evaluationQueryFailed) refetchEvaluation();
    },
    rounds,
    awardLettersSent: awardLetters == null
      ? null
      : awardLetters.letters.every((letter) => letter.status === AwardLetterStatusDto.Sent),
    awardLettersApproved: awardLetters == null
      ? null
      : awardLetters.letters.length > 0 &&
        awardLetters.letters.every((letter) => letter.status === AwardLetterStatusDto.Approved),
    activeTab,
    focusModeEnabled: requireBooleanEnvVar("VITE_ENABLE_FOCUS_MODE"),
    mayCreateContract,
    mayManage,
    meId: me.id,
    juryReportHasUnsavedChanges,
    frameworkAgreementId: frameworkAgreementId ?? null,
    frameworkAgreementHiddenFromReader: frameworkAgreementLink?.mayOpen === false,
    callOffRefusedByTermination,
    unawardedRefusedByFramework,
    frameworkAgreementsLoaded,
  };
};
